import { useState, useRef, useCallback } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Code,
  Send,
  RefreshCw,
  Database,
  Server,
  CheckCircle,
  XCircle,
  Copy,
  Clock,
  ChevronRight,
  ChevronDown,
  FileText,
  Upload,
  X,
  FileJson,
  Download,
} from 'lucide-react';
import { clsx } from 'clsx';

const API_BASE = '/api';

// Types
interface ApiEndpoint {
  path: string;
  method: string;
  description: string;
  category: string;
  requestBodySchema?: Record<string, unknown>;
  queryParams?: { name: string; type: string; required: boolean; description: string }[];
}

interface TestResponse {
  status: number;
  statusText: string;
  headers: Record<string, string>;
  body: unknown;
  durationMs: number;
}

interface PdfExtractionResult {
  filename: string;
  extraction: {
    text: string;
    numPages: number;
    numRenderedPages: number;
    info: Record<string, unknown>;
    metadata: Record<string, unknown>;
  };
  stats: {
    textLength: number;
    lineCount: number;
    wordCount: number;
  };
  durationMs: number;
}

interface SystemInfo {
  server: {
    nodeVersion: string;
    platform: string;
    uptime: number;
    env: string;
  };
  database: {
    version: string;
    connected: boolean;
  };
  services: {
    unleashed: { configured: boolean };
    openai: { available: boolean; error?: string };
  };
  stats: {
    orders: number;
    customers: number;
    products: number;
  };
}

const methodColors: Record<string, string> = {
  GET: 'bg-green-100 text-green-700',
  POST: 'bg-blue-100 text-blue-700',
  PUT: 'bg-yellow-100 text-yellow-700',
  PATCH: 'bg-orange-100 text-orange-700',
  DELETE: 'bg-red-100 text-red-700',
};

export default function DeveloperPage() {
  const [selectedEndpoint, setSelectedEndpoint] = useState<ApiEndpoint | null>(null);
  const [requestBody, setRequestBody] = useState('');
  const [response, setResponse] = useState<TestResponse | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

  // PDF Extraction state
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfResult, setPdfResult] = useState<PdfExtractionResult | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showRawJson, setShowRawJson] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch endpoints
  const { data: endpointsData, isLoading: loadingEndpoints } = useQuery<{ data: { endpoints: ApiEndpoint[]; grouped: Record<string, ApiEndpoint[]> } }>({
    queryKey: ['endpoints'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/dev/endpoints`);
      return res.json();
    },
  });

  // Fetch system info
  const { data: systemInfo, isLoading: loadingSystem, refetch: refetchSystem } = useQuery<{ data: SystemInfo }>({
    queryKey: ['systemInfo'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/dev/system-info`);
      return res.json();
    },
    refetchInterval: 30000,
  });

  // Test request mutation
  const testRequest = useMutation({
    mutationFn: async ({ method, endpoint, body }: { method: string; endpoint: string; body?: string }) => {
      const res = await fetch(`${API_BASE}/dev/test-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          method,
          endpoint,
          body: body ? JSON.parse(body) : undefined,
        }),
      });
      return res.json();
    },
    onSuccess: (data) => {
      setResponse(data.data);
    },
  });

  // PDF extraction mutation
  const extractPdf = useMutation({
    mutationFn: async ({ fileBase64, filename }: { fileBase64: string; filename: string }) => {
      const res = await fetch(`${API_BASE}/dev/extract-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileBase64, filename }),
      });
      return res.json();
    },
    onSuccess: (data) => {
      if (data.success) {
        setPdfResult(data.data);
        setPdfError(null);
      } else {
        setPdfError(data.error?.message || 'Extraction failed');
        setPdfResult(null);
      }
    },
    onError: (error) => {
      setPdfError(error instanceof Error ? error.message : 'Extraction failed');
      setPdfResult(null);
    },
  });

  // Handle PDF file selection
  const handlePdfFile = useCallback((file: File) => {
    if (file.type !== 'application/pdf') {
      setPdfError('Please select a PDF file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setPdfError('File size must be less than 10MB');
      return;
    }
    setPdfFile(file);
    setPdfError(null);
    setPdfResult(null);

    // Convert to base64 and extract
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];
      extractPdf.mutate({ fileBase64: base64, filename: file.name });
    };
    reader.onerror = () => {
      setPdfError('Failed to read file');
    };
    reader.readAsDataURL(file);
  }, [extractPdf]);

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handlePdfFile(file);
  }, [handlePdfFile]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handlePdfFile(file);
  }, [handlePdfFile]);

  const clearPdfResult = useCallback(() => {
    setPdfFile(null);
    setPdfResult(null);
    setPdfError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleSelectEndpoint = (endpoint: ApiEndpoint) => {
    setSelectedEndpoint(endpoint);
    setResponse(null);
    if (endpoint.requestBodySchema) {
      setRequestBody(JSON.stringify(endpoint.requestBodySchema, null, 2));
    } else {
      setRequestBody('');
    }
  };

  const handleTest = () => {
    if (!selectedEndpoint) return;
    testRequest.mutate({
      method: selectedEndpoint.method,
      endpoint: selectedEndpoint.path,
      body: requestBody || undefined,
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatUptime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const grouped = endpointsData?.data?.grouped || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Developer Tools</h2>
          <p className="text-gray-500 mt-1">API Explorer & System Information</p>
        </div>
        <button
          onClick={() => refetchSystem()}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className={clsx('w-4 h-4', loadingSystem && 'animate-spin')} />
          Refresh Status
        </button>
      </div>

      {/* System Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Server className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Server</p>
              <p className="font-semibold text-gray-900">
                {systemInfo?.data?.server?.env || 'Loading...'}
              </p>
              {systemInfo?.data?.server?.uptime && (
                <p className="text-xs text-gray-400">
                  Uptime: {formatUptime(systemInfo.data.server.uptime)}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className={clsx('p-2 rounded-lg', systemInfo?.data?.database?.connected ? 'bg-green-50' : 'bg-red-50')}>
              <Database className={clsx('w-5 h-5', systemInfo?.data?.database?.connected ? 'text-green-600' : 'text-red-600')} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Database</p>
              <p className="font-semibold text-gray-900">
                {systemInfo?.data?.database?.connected ? 'Connected' : 'Disconnected'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className={clsx('p-2 rounded-lg', systemInfo?.data?.services?.unleashed?.configured ? 'bg-green-50' : 'bg-yellow-50')}>
              {systemInfo?.data?.services?.unleashed?.configured ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <XCircle className="w-5 h-5 text-yellow-600" />
              )}
            </div>
            <div>
              <p className="text-sm text-gray-500">Unleashed API</p>
              <p className="font-semibold text-gray-900">
                {systemInfo?.data?.services?.unleashed?.configured ? 'Configured' : 'Not Configured'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className={clsx('p-2 rounded-lg', systemInfo?.data?.services?.openai?.available ? 'bg-green-50' : 'bg-yellow-50')}>
              {systemInfo?.data?.services?.openai?.available ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <XCircle className="w-5 h-5 text-yellow-600" />
              )}
            </div>
            <div>
              <p className="text-sm text-gray-500">OpenAI API</p>
              <p className="font-semibold text-gray-900">
                {systemInfo?.data?.services?.openai?.available ? 'Available' : 'Not Configured'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      {systemInfo?.data?.stats && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-perfectted-light rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-perfectted-dark">{systemInfo.data.stats.orders}</p>
            <p className="text-sm text-perfectted-primary">Orders</p>
          </div>
          <div className="bg-blue-50 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-blue-700">{systemInfo.data.stats.customers}</p>
            <p className="text-sm text-blue-600">Customers</p>
          </div>
          <div className="bg-purple-50 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-purple-700">{systemInfo.data.stats.products}</p>
            <p className="text-sm text-purple-600">Products</p>
          </div>
        </div>
      )}

      {/* PDF Extraction */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            PDF Extraction
          </h3>
          {pdfFile && (
            <button
              onClick={clearPdfResult}
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <X className="w-4 h-4" />
              Clear
            </button>
          )}
        </div>

        <div className="p-4">
          {/* Upload area */}
          {!pdfFile && (
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={clsx(
                'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
                isDragging
                  ? 'border-perfectted-primary bg-perfectted-light'
                  : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
              )}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleFileInputChange}
                className="hidden"
              />
              <Upload className={clsx('w-10 h-10 mx-auto mb-3', isDragging ? 'text-perfectted-primary' : 'text-gray-400')} />
              <p className="text-gray-600 font-medium">
                {isDragging ? 'Drop your PDF here' : 'Drag & drop a PDF file here'}
              </p>
              <p className="text-gray-400 text-sm mt-1">or click to browse (max 10MB)</p>
            </div>
          )}

          {/* Loading state */}
          {pdfFile && extractPdf.isPending && (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 text-perfectted-primary mx-auto animate-spin mb-3" />
              <p className="text-gray-600">Extracting text from {pdfFile.name}...</p>
            </div>
          )}

          {/* Error state */}
          {pdfError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-700 font-medium">Extraction Failed</p>
                <p className="text-red-600 text-sm">{pdfError}</p>
              </div>
            </div>
          )}

          {/* Results */}
          {pdfResult && (
            <div className="space-y-4">
              {/* File info */}
              <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-3">
                  <FileText className="w-8 h-8 text-red-500" />
                  <div>
                    <p className="font-medium text-gray-900">{pdfResult.filename}</p>
                    <p className="text-sm text-gray-500">
                      {pdfResult.extraction.numPages} page{pdfResult.extraction.numPages !== 1 ? 's' : ''} ·{' '}
                      {pdfResult.stats.wordCount.toLocaleString()} words ·{' '}
                      {pdfResult.durationMs}ms
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowRawJson(!showRawJson)}
                    className={clsx(
                      'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                      showRawJson
                        ? 'bg-perfectted-light text-perfectted-dark'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    )}
                  >
                    <FileJson className="w-4 h-4" />
                    JSON
                  </button>
                  <button
                    onClick={() => {
                      const blob = new Blob([pdfResult.extraction.text], { type: 'text/plain' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${pdfResult.filename.replace('.pdf', '')}_extracted.txt`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-100 text-gray-600 hover:bg-gray-200"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                </div>
              </div>

              {/* Stats bar */}
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-blue-700">{pdfResult.extraction.numPages}</p>
                  <p className="text-xs text-blue-600">Pages</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-green-700">{pdfResult.stats.wordCount.toLocaleString()}</p>
                  <p className="text-xs text-green-600">Words</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-purple-700">{pdfResult.stats.lineCount.toLocaleString()}</p>
                  <p className="text-xs text-purple-600">Lines</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-orange-700">{pdfResult.stats.textLength.toLocaleString()}</p>
                  <p className="text-xs text-orange-600">Characters</p>
                </div>
              </div>

              {/* Content display */}
              {showRawJson ? (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-gray-700">Full JSON Response</h4>
                    <button
                      onClick={() => copyToClipboard(JSON.stringify(pdfResult, null, 2))}
                      className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                    >
                      <Copy className="w-3 h-3" />
                      Copy
                    </button>
                  </div>
                  <pre className="font-mono text-xs bg-gray-50 p-3 rounded-lg overflow-x-auto max-h-96 border border-gray-200">
                    {JSON.stringify(pdfResult, null, 2)}
                  </pre>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-gray-700">Extracted Text</h4>
                    <button
                      onClick={() => copyToClipboard(pdfResult.extraction.text)}
                      className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                    >
                      <Copy className="w-3 h-3" />
                      Copy
                    </button>
                  </div>
                  <pre className="font-mono text-xs bg-gray-50 p-3 rounded-lg overflow-x-auto max-h-96 whitespace-pre-wrap border border-gray-200">
                    {pdfResult.extraction.text}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* API Explorer */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Endpoints List */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Code className="w-4 h-4" />
              API Endpoints
            </h3>
          </div>
          <div className="max-h-[600px] overflow-y-auto">
            {loadingEndpoints ? (
              <div className="p-4 text-center text-gray-500">Loading...</div>
            ) : (
              Object.entries(grouped).map(([category, endpoints]) => (
                <div key={category} className="border-b border-gray-100 last:border-0">
                  <button
                    onClick={() => toggleCategory(category)}
                    className="w-full px-4 py-2 flex items-center justify-between hover:bg-gray-50"
                  >
                    <span className="font-medium text-gray-700">{category}</span>
                    {expandedCategories[category] ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                  {expandedCategories[category] && (
                    <div className="px-2 pb-2 space-y-1">
                      {(endpoints as ApiEndpoint[]).map((endpoint, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleSelectEndpoint(endpoint)}
                          className={clsx(
                            'w-full px-3 py-2 rounded-lg text-left text-sm transition-colors',
                            selectedEndpoint?.path === endpoint.path
                              ? 'bg-perfectted-light text-perfectted-dark'
                              : 'hover:bg-gray-50'
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <span className={clsx('px-2 py-0.5 rounded text-xs font-medium', methodColors[endpoint.method])}>
                              {endpoint.method}
                            </span>
                            <span className="font-mono text-xs truncate">{endpoint.path}</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1 truncate">{endpoint.description}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Request Builder */}
        <div className="lg:col-span-2 space-y-4">
          {/* Request */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Request</h3>
              <button
                onClick={handleTest}
                disabled={!selectedEndpoint || testRequest.isPending}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-white bg-perfectted-primary rounded-lg hover:bg-perfectted-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {testRequest.isPending ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Send
              </button>
            </div>
            <div className="p-4">
              {selectedEndpoint ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <span className={clsx('px-3 py-1 rounded text-sm font-medium', methodColors[selectedEndpoint.method])}>
                      {selectedEndpoint.method}
                    </span>
                    <code className="flex-1 font-mono text-sm bg-gray-100 px-3 py-1 rounded">
                      {selectedEndpoint.path}
                    </code>
                    <button
                      onClick={() => copyToClipboard(selectedEndpoint.path)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-sm text-gray-600">{selectedEndpoint.description}</p>

                  {selectedEndpoint.queryParams && selectedEndpoint.queryParams.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Query Parameters</h4>
                      <div className="text-xs space-y-1">
                        {selectedEndpoint.queryParams.map((param) => (
                          <div key={param.name} className="flex items-start gap-2">
                            <code className="bg-gray-100 px-1.5 py-0.5 rounded">{param.name}</code>
                            <span className="text-gray-500">({param.type})</span>
                            {param.required && <span className="text-red-500">*</span>}
                            <span className="text-gray-400">{param.description}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {['POST', 'PUT', 'PATCH'].includes(selectedEndpoint.method) && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Request Body</h4>
                      <textarea
                        value={requestBody}
                        onChange={(e) => setRequestBody(e.target.value)}
                        placeholder="Enter JSON request body..."
                        rows={8}
                        className="w-full font-mono text-sm px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-perfectted-primary"
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Select an endpoint to test
                </div>
              )}
            </div>
          </div>

          {/* Response */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Response</h3>
              {response && (
                <div className="flex items-center gap-3 text-sm">
                  <span className={clsx(
                    'px-2 py-0.5 rounded font-medium',
                    response.status >= 200 && response.status < 300
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  )}>
                    {response.status} {response.statusText}
                  </span>
                  <span className="flex items-center gap-1 text-gray-500">
                    <Clock className="w-3 h-3" />
                    {response.durationMs}ms
                  </span>
                </div>
              )}
            </div>
            <div className="p-4">
              {response ? (
                <pre className="font-mono text-xs bg-gray-50 p-3 rounded-lg overflow-x-auto max-h-96">
                  {JSON.stringify(response.body, null, 2)}
                </pre>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Response will appear here
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
